import { ForwardedRef, ReactElement, forwardRef, useCallback, useEffect, useState } from 'react';

import Button from '@/common/Button';
import Group from '@/common/Group';
import { QuerySelectTypes } from '@/common/QuerySelect/types';
import Select from '@/common/Select';
import Tippy from '@tippyjs/react';
import { getClasses } from '@/utils';

const QuerySelect = forwardRef(
  ({ query, lazy, showRefreshButton, ...props }: QuerySelectTypes.Props, ref: ForwardedRef<QuerySelectTypes.RefType>): ReactElement => {
    const [loaded, setLoaded] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [options, setOptions] = useState<QuerySelectTypes.BaseProps['children']>([]);

    const getOptions = useCallback(async (): Promise<void> => {
      try {
        setLoading(true);
        const res = await query();
        setOptions(res);
        setError(null);
      } catch (err) {
        setError(err.message || 'Failed to load options');
      } finally {
        setLoading(false);
        setLoaded(true);
      }
    }, [query]);

    useEffect((): void => {
      if (lazy !== undefined && lazy !== false) return;
      getOptions();
    }, [getOptions, lazy]);

    const Control = (
      <Select
        {...props}
        disabled={(props.disabled !== undefined && props.disabled !== false) || loading}
        className={getClasses('QuerySelect', 'Common', props.className)}
        onFocus={(value, event) => {
          if (lazy !== undefined && lazy !== false && !loaded) getOptions();
          if (props.onFocus) {
            props.onFocus(value, event);
          }
        }}
        ref={ref}
      >
        {options}
      </Select>
    );

    if (showRefreshButton === undefined || showRefreshButton === false) return Control;
    return (
      <Group>
        {Control}
        <Tippy className="tippy-dark" content={error || 'Fetch Options'} placement="top">
          <Button variant="outline-primary" disabled={loading} onClick={getOptions}>
            <i className={getClasses('fa', loading ? 'fa-spinner fa-pulse' : error ? 'fa-warning text-danger' : 'fa-refresh')} />
          </Button>
        </Tippy>
      </Group>
    );
  }
);
QuerySelect.displayName = 'QuerySelect';

export default QuerySelect;
